<template>
  <!-- Global connectivity banner: device offline (persistent) or a failed
       request to the server (transient). Sits above everything, clear of the
       status bar via the safe-area inset. -->
  <div
    v-if="!online || netError"
    class="int-conn-banner fixed inset-x-0 top-0 z-[20000] flex items-center justify-center gap-1.5 px-3 pb-1.5 text-xs font-semibold text-white"
    :class="online ? 'bg-amber-500' : 'bg-rose-600'"
    :style="{ paddingTop: 'calc(6px + env(safe-area-inset-top, 0px))' }"
  >
    <v-icon size="15">{{ online ? 'mdi-cloud-alert-outline' : 'mdi-wifi-off' }}</v-icon>
    <span>{{ online ? "Can't reach the server — retrying…" : "You're offline — reconnect to load the latest." }}</span>
  </div>
  <router-view />
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      // Connectivity UI state (see mounted for the listeners that drive these).
      online: (typeof navigator !== 'undefined' ? navigator.onLine !== false : true),
      netError: false,
    };
  },
  mounted() {
    // Device connectivity (fires in the Capacitor WebView and the browser).
    this._onOnline = () => { this.online = true; };
    this._onOffline = () => { this.online = false; };
    window.addEventListener('online', this._onOnline);
    window.addEventListener('offline', this._onOffline);
    // A failed API call (no response) dispatches this from the axios
    // interceptor; show a brief "can't reach server" hint, then clear it.
    this._onNetError = () => {
      this.netError = true;
      if (this._netTimer) clearTimeout(this._netTimer);
      this._netTimer = setTimeout(() => { this.netError = false; }, 4000);
    };
    window.addEventListener('int:neterror', this._onNetError);

    // Follow the device's light/dark appearance (native iOS feel).
    const mq = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    if (mq) {
      this._mq = mq;
      this._onScheme = (e) => this.setTheme(e.matches);
      this.setTheme(mq.matches);
      if (mq.addEventListener) mq.addEventListener('change', this._onScheme);
      else if (mq.addListener) mq.addListener(this._onScheme);
    }

    // On phones our tables stack into cards (the header row is hidden by CSS).
    // Copy each column's header text onto its cells as data-label so the CSS
    // can print the label beside every value — for EVERY .int-table, app-wide.
    this._labelTables = () => this.labelTables();
    this.labelTables();
    if (window.MutationObserver) {
      this._tableObserver = new MutationObserver(() => {
        if (this._labelRaf) cancelAnimationFrame(this._labelRaf);
        this._labelRaf = requestAnimationFrame(this._labelTables);
      });
      this._tableObserver.observe(document.body, { childList: true, subtree: true });
    }
  },
  beforeUnmount() {
    if (this._mq) {
      if (this._mq.removeEventListener) this._mq.removeEventListener('change', this._onScheme);
      else if (this._mq.removeListener) this._mq.removeListener(this._onScheme);
    }
    if (this._tableObserver) this._tableObserver.disconnect();
    if (this._labelRaf) cancelAnimationFrame(this._labelRaf);
    window.removeEventListener('online', this._onOnline);
    window.removeEventListener('offline', this._onOffline);
    window.removeEventListener('int:neterror', this._onNetError);
    if (this._netTimer) clearTimeout(this._netTimer);
  },
  methods: {
    setTheme(dark) {
      try {
        this.$vuetify.theme.global.name.value = dark ? 'intDark' : 'intLight';
      } catch (e) { /* theme not ready — ignore */ }
    },
    // Stamp data-label onto each cell from its column header. Observing only
    // childList (not attributes) means setting these attrs won't re-trigger us.
    labelTables() {
      const tables = document.querySelectorAll('.int-table table');
      tables.forEach((table) => {
        const heads = table.querySelectorAll('thead th');
        if (!heads.length) return;
        const labels = Array.from(heads).map((th) => (th.textContent || '').trim());
        table.querySelectorAll('tbody tr').forEach((tr) => {
          Array.from(tr.children).forEach((td, i) => {
            const label = labels[i];
            if (label) td.setAttribute('data-label', label);
            else td.removeAttribute('data-label');
          });
        });
      });
    },
  },
};
</script>

<style>
/* =========================================================================
   INT Ticketing — Design System foundation
   One place for tokens (color, space, radius, type, elevation) plus the
   small set of utility classes every page shares. Page components should
   lean on these instead of re-declaring their own values.
   ========================================================================= */
:root {
  /* Brand (mirrored from the Claims CMS) */
  --int-yellow: #FDCC0E;
  --int-yellow-dk: #D9A400;
  --int-yellow-soft: rgba(253, 204, 14, 0.12);
  --int-black: #121212;
  --int-grey-bg: #f4f5f7;
  --int-border: #EDEEF1;

  /* Sidebar (cream) */
  --int-sidebar: #FBEFC2;
  --int-sidebar-active: #1F6BFF;

  /* Text scale */
  --int-text-strong: #161616;
  --int-text: #3a3b40;
  --int-text-muted: #70727a;
  --int-text-faint: #9a9ba0;

  /* Surfaces */
  --int-surface: #ffffff;
  --int-surface-muted: #f7f8fa;

  /* Spacing scale (4px base) */
  --int-space-1: 4px;
  --int-space-2: 8px;
  --int-space-3: 12px;
  --int-space-4: 16px;
  --int-space-5: 24px;
  --int-space-6: 32px;

  /* Radius scale */
  --int-radius-sm: 8px;
  --int-radius-md: 10px;
  --int-radius-lg: 14px;

  /* Elevation scale — soft, modern, low-contrast */
  --int-shadow-sm: 0 1px 2px rgba(17, 17, 26, 0.035), 0 0 1px rgba(17, 17, 26, 0.06);
  --int-shadow-md: 0 2px 8px rgba(17, 17, 26, 0.06), 0 1px 2px rgba(17, 17, 26, 0.04);
  --int-shadow-lg: 0 10px 30px rgba(17, 17, 26, 0.10);

  /* Type scale */
  --int-fs-display: 1.5rem;
  --int-fs-title: 1.2rem;
  --int-fs-h2: 1.02rem;
  --int-fs-body: 0.9rem;
  --int-fs-sm: 0.82rem;
  --int-fs-xs: 0.72rem;

  /* Layout */
  --int-content-max: 1680px;
}

html,
body,
#app {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  color: var(--int-text);
  letter-spacing: 0.1px;
}

#app {
  background: var(--int-grey-bg);
  min-height: 100vh;
}

/* Compact page gutters: full width with a small, consistent padding so pages
   use the available space instead of large centered margins. */
.v-main .v-container {
  max-width: 100% !important;
  padding: 12px 14px !important;
}

/* Buttons read as words, not shouting. */
.v-btn {
  text-transform: none !important;
  letter-spacing: 0.01em !important;
  font-weight: 600;
}
.v-btn--variant-flat.bg-primary,
.v-btn--variant-elevated.text-primary {
  box-shadow: 0 1px 2px rgba(253, 204, 14, 0.35);
}

/* Subtle, modern card surfaces: hairline border + soft resting shadow. */
.v-card {
  border-color: var(--int-border) !important;
  box-shadow: var(--int-shadow-sm) !important;
  transition: box-shadow 0.18s ease, transform 0.18s ease, border-color 0.18s ease;
}
.v-card-title {
  font-weight: 700 !important;
  letter-spacing: 0.005em;
  color: var(--int-text-strong);
}

/* Opt-in interactive card: adds the hover lift used by stat/list cards. */
.int-hoverable {
  cursor: pointer;
}
.int-hoverable:hover {
  transform: translateY(-2px);
  box-shadow: var(--int-shadow-md) !important;
  border-color: #e2e4ea !important;
}

/* Inputs: gentler rounding + a calm brand focus ring. */
.v-field {
  border-radius: var(--int-radius-md) !important;
}
.v-field--focused .v-field__outline {
  --v-field-border-opacity: 1;
}
.v-field--variant-outlined.v-field--focused {
  box-shadow: 0 0 0 3px rgba(253, 204, 14, 0.16);
  border-radius: var(--int-radius-md);
}

/* Chips: soft, pill-ish, medium weight. */
.v-chip { font-weight: 600; }

/* Dialogs read as elevated sheets. */
.v-overlay__content > .v-card {
  border-radius: var(--int-radius-lg) !important;
  box-shadow: var(--int-shadow-lg) !important;
}

/* List items: quiet hover, rounded. */
.v-list-item { border-radius: var(--int-radius-sm); }

/* ---- Shared typography helpers ---- */
.section-title {
  font-weight: 700;
  font-size: var(--int-fs-h2);
  letter-spacing: 0.01em;
  color: var(--int-text-strong);
}
.section-sub {
  color: var(--int-text-muted);
  font-size: var(--int-fs-sm);
}

/* ---- Page header rhythm (used by PageHeader.vue) ---- */
.int-page-h {
  font-size: var(--int-fs-title);
  font-weight: 800;
  line-height: 1.1;
  color: var(--int-text-strong);
}
.int-page-desc {
  font-size: var(--int-fs-sm);
  color: var(--int-text-muted);
}

/* ---- Consistent data-table chrome everywhere ----
   Works whether `.int-table` sits on the v-data-table itself or on a
   wrapping div; styles the real th/tr/td so it also covers custom
   item-slot rows. Container border/radius is left to the parent v-card. */
.int-table th {
  font-weight: 600 !important;
  color: var(--int-text-muted) !important;
  background: #FAFAFB;
  white-space: nowrap;
  font-size: var(--int-fs-xs) !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-bottom: 1px solid var(--int-border) !important;
}
.int-table td {
  font-size: var(--int-fs-sm);
  vertical-align: middle;
  border-bottom: 1px solid #f1f2f5 !important;
}
/* Horizontal scroll on narrow screens instead of overflowing the layout. */
.int-table .v-table__wrapper {
  overflow-x: auto;
}
/* Optional vertical scroll container so headers can stick on long lists. */
.int-table-scroll {
  max-height: calc(100vh - 320px);
  overflow: auto;
}
/* Sticky header when the table body scrolls (works with fixed-header too). */
.int-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
}
/* Clean, Ant-style rows: no zebra, just a calm neutral hover. */
.int-table tbody tr:hover td {
  background: var(--int-surface-muted);
}
/* Don't hover the empty-state or loading helper rows. */
.int-table tbody tr.v-data-table-rows-no-data td,
.int-table tbody tr.v-data-table-progress td {
  background: transparent;
}

/* ---- Phone view: tables collapse into stacked cards (Vuetify mobile mode) ----
   Vuetify renders each row as a stacked label/value block below the `sm`
   breakpoint; we give each row a card outline and drop the nested scroll. */
@media (max-width: 600px) {
  /* Larger base type on phones for readability — scales every rem-based size
     (Tailwind text-xs/sm/base, Vuetify, etc.) up ~13% without touching layout. */
  html {
    font-size: 18px;
  }
  .int-table-scroll {
    max-height: none !important;
    overflow: visible !important;
  }
  /* Let the table use the full width and flow as blocks (not a wide table). */
  .int-table .v-table__wrapper {
    overflow: visible !important;
  }
  .int-table table {
    width: 100% !important;
    display: block;
  }
  .int-table tbody {
    display: block;
    width: 100%;
  }
  /* The column-header row is redundant once rows are cards. */
  .int-table thead {
    display: none !important;
  }
  .int-table tbody tr {
    display: block;
    width: 100%;
    border: 1px solid var(--int-border);
    border-radius: var(--int-radius-md);
    background: var(--int-surface);
    margin-bottom: 10px;
    padding: 2px 8px;
    box-shadow: var(--int-shadow-sm);
  }
  .int-table tbody tr:hover td {
    background: transparent;
  }
  /* Each field on its own full-width row. */
  .int-table tbody td {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100% !important;
    max-width: 100% !important;
    text-align: right;
    border-bottom: 1px solid #f1f2f5 !important;
    padding: 10px 4px !important;
    min-height: 44px;
  }
  /* Column header printed on the left of every stacked cell (see labelTables
     in App.vue's script, which stamps data-label onto each cell). */
  .int-table tbody td[data-label]::before {
    content: attr(data-label);
    flex: 0 0 auto;
    margin-right: auto;
    padding-right: 12px;
    text-align: left;
    font-weight: 600;
    font-size: 0.78rem;
    color: var(--int-text-faint);
    white-space: nowrap;
  }
  .int-table tbody tr td:last-child {
    border-bottom: none !important;
  }
}

/* ---- Accessibility: visible keyboard focus ---- */
a:focus-visible,
button:focus-visible,
.v-btn:focus-visible,
[tabindex]:focus-visible {
  outline: 2px solid var(--int-yellow);
  outline-offset: 2px;
  border-radius: var(--int-radius-sm);
}

/* Respect users who prefer reduced motion. */
@media (prefers-reduced-motion: reduce) {
  * {
    transition-duration: 0.001ms !important;
    animation-duration: 0.001ms !important;
  }
}

/* Slim, unobtrusive scrollbars. */
::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}
::-webkit-scrollbar-thumb {
  background: #c7c9d1;
  border-radius: 8px;
  border: 2px solid transparent;
  background-clip: content-box;
}
::-webkit-scrollbar-thumb:hover {
  background: #aeb0ba;
  background-clip: content-box;
}
::-webkit-scrollbar-track {
  background: transparent;
}

/* ============================================================
   iOS-native touch polish
   ============================================================ */
* {
  -webkit-tap-highlight-color: transparent;
}
html, body {
  -webkit-overflow-scrolling: touch;
}
/* Comfortable minimum tap targets on touch devices (Apple HIG: 44pt). */
@media (pointer: coarse) {
  .v-btn { min-height: 44px; }
  .v-btn.v-btn--icon { min-width: 44px; min-height: 44px; }
  .v-list-item { min-height: 44px; }
}

/* ============================================================
   Dark mode — follows the device appearance. Tokens flip here;
   Vuetify components flip via the intDark theme (App.vue script).
   ============================================================ */
@media (prefers-color-scheme: dark) {
  :root {
    --int-grey-bg: #0f1115;
    --int-border: #2a2e37;
    --int-surface: #1a1d23;
    --int-surface-muted: #22262e;
    --int-text-strong: #f2f3f5;
    --int-text: #d6d8de;
    --int-text-muted: #9a9ba0;
    --int-text-faint: #7c7e86;
    --int-sidebar: #15171c;
    --int-yellow-soft: rgba(253, 204, 14, 0.16);
  }

  /* Tables */
  .int-table th { background: #22262e; }
  .int-table td { border-bottom-color: #23262d !important; }
  .int-table tbody tr:hover td { background: #22262e; }
  .int-table tbody tr { background: var(--int-surface); }

  /* Sidebar (its scoped styles hard-code light colors — override for dark). */
  .int-sidebar .int-nav-item { color: #c9cbd1 !important; }
  .int-sidebar .int-nav-item .v-icon { color: #9a9ba0 !important; }
  .int-sidebar .int-nav-item:hover:not(.v-list-item--active) { background: rgba(255, 255, 255, 0.06) !important; }
  .int-sidebar .int-nav-item.v-list-item--active { background: #2a2e37 !important; color: #ffffff !important; }
  .int-sidebar .int-nav-item.v-list-item--active .v-icon { color: #ffffff !important; }
  .int-sidebar .int-subheader { color: rgba(255, 255, 255, 0.45) !important; }
  .int-sidebar .int-brand-plate { border-bottom-color: rgba(255, 255, 255, 0.08) !important; }
  .int-sidebar .int-logo-plate { background: #22262e !important; }
}

/* Scrollable regions keep momentum scrolling on iOS. */
.int-table-scroll,
.gantt-scroll {
  -webkit-overflow-scrolling: touch;
}

/* ============================================================
   Safe areas — keep the top bar clear of the status bar / Dynamic Island.
   viewport-fit=cover (index.html) makes env(safe-area-inset-*) report the
   real device insets; we pad the app bar down by that amount and shift the
   main content to match so nothing hides behind the taller bar.
   ============================================================ */
/* Push the toolbar's actual content row (burger, title, actions) below the
   status bar / Dynamic Island. Padding the wrapper alone doesn't move the
   inner content, so we target .v-toolbar__content directly. */
.int-appbar .v-toolbar__content {
  height: auto !important;
  min-height: 64px;
  padding-top: env(safe-area-inset-top, 0px) !important;
}
/* Shift main content down so it isn't hidden behind the taller bar. */
.int-main {
  margin-top: env(safe-area-inset-top, 0px);
}
/* The nav drawer's brand plate should also clear the status bar when open. */
.int-sidebar .int-brand-plate {
  padding-top: calc(16px + env(safe-area-inset-top, 0px));
}
</style>
